/**
 * What the header search searches while a rave is open.
 *
 * The header search is the SHELL's (`HeaderSearch.tsx`), and the shell is
 * mounted ABOVE `EditionProvider` — so it cannot read the edition, and the
 * rave cannot hand it a prop. The rave publishes its search here instead, the
 * way it once published its languages: set on mount, cleared
 * on the way out, so every other host's header search is the platform's again.
 *
 * The search runs on the bundle already on the phone — no request, so it
 * works in a field with no signal, which is where most searching happens.
 *
 * `null` means "no rave open": search the platform.
 */
import { useEffect, useSyncExternalStore } from 'react';

/** One row of the rave's results, already worded for the dropdown. */
export interface RaveHit {
  /**
   * A genre or a country is a row too (the 0.14.82 walk: "genre doesn't let you select the genre,
   * only artists"; "it doesn't let you choose the country and open search results"): pressed, it
   * opens every act of that genre or country.
   */
  readonly kind: 'act' | 'stage' | 'genre' | 'country' | 'place';
  /** The act's slug, the stage's id, the genre or the country code — what `open` is handed back. */
  readonly key: string;
  readonly label: string;
  /** "Wed 29 Jul · 20:00 · Main Stage" for an act; the track for a stage. */
  readonly detail: string | null;
  /** The stage's colour — data, not a token. */
  readonly color: string | null;
  /** Country flags, already emoji. */
  readonly flags: string;
}

export interface RaveSearchScope {
  /** The edition's name — "O.Z.O.R.A 2026" — for the field and the hint. */
  readonly name: string;
  search(term: string): { acts: RaveHit[]; stages: RaveHit[]; genres?: RaveHit[]; countries?: RaveHit[]; places?: RaveHit[] };
  open(hit: RaveHit): void;
  /**
   * Count a SETTLED query as the rave's `search` statistics event (§62.2) —
   * with the full match count, not the dropdown's six. The header calls it
   * once typing has stopped, never per keystroke.
   */
  record(term: string): void;
}

let current: RaveSearchScope | null = null;
const listeners = new Set<() => void>();

function set(next: RaveSearchScope | null): void {
  current = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The open rave's search, or null outside a rave. */
export function useRaveSearchScope(): RaveSearchScope | null {
  return useSyncExternalStore(subscribe, () => current, () => null);
}

/**
 * Make `scope` the header search's while the caller is mounted. Memoise it.
 * `null` publishes nothing: a rave that runs no `search` module leaves the
 * header on the platform's search.
 */
export function usePublishRaveSearchScope(scope: RaveSearchScope | null): void {
  useEffect(() => {
    if (!scope) return undefined;
    set(scope);
    return () => { if (current === scope) set(null); };
  }, [scope]);
}

/** The "Show all N acts" row's key — no slug has a `*` in it. */
export const SHOW_ALL_KEY = '*all';

/**
 * The rave's results PAGE (the user, 2026-11-02: "search on this rave also should open search page results").
 * The header asks for it — Enter, "Show all N acts", a genre or a country, "Search {rave} for …" from a page —
 * and the rave draws it, because the rave holds the line-up, the saves and the jump to a set. One store, both
 * sides: the header writes the words as they are typed, the page's ✕ clears it.
 */
export interface RaveResultsRequest {
  /**
   * Which of the two searches (the user, 2026-11-02: "1 search inside the rave (should work offline), 1 search all
   * MyRave (online only)"): the rave's, from its bundle, drawn by the rave — or all of MyRave's, from the server,
   * drawn by the header. One page, two tabs, the same words.
   */
  readonly where: 'rave' | 'all';
  readonly term: string;
  /** A genre or a country chosen in the dropdown — the page opens narrowed to it. */
  readonly filter: { readonly kind: 'genre' | 'country'; readonly key: string } | null;
}

let results: RaveResultsRequest | null = null;
const resultsListeners = new Set<() => void>();

export function showRaveResults(next: RaveResultsRequest | null): void {
  results = next;
  for (const l of resultsListeners) l();
}

/** What the rave's results page shows, or null while it is closed. */
export function useRaveResults(): RaveResultsRequest | null {
  return useSyncExternalStore(
    (l) => { resultsListeners.add(l); return () => { resultsListeners.delete(l); }; },
    () => results,
    () => null,
  );
}
